import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star } from "@phosphor-icons/react";
import { BUSINESS } from "@/lib/siteData";

const LINES = ["PRECISION", "FIREARMS &", "GUNSMITHING"];

const lineWrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const gunY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const gunScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollNext = () => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" ref={ref} className="relative min-h-screen w-full overflow-hidden bg-transparent">
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-ink/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-federal/20" />
        <div className="absolute inset-0 scanlines opacity-25" />
      </motion.div>

      <motion.div
        style={{ y: gunY, scale: gunScale }}
        className="absolute right-0 bottom-0 z-10 w-[62%] md:w-[46%] max-w-[720px] pointer-events-none"
      >
        <img
          src="https://static.prod-images.emergentagent.com/jobs/88f3cf28-08a6-4c4d-a110-4a623efba473/images/4ae1013585e1f24cfff72592a1d6ff5ad562bbaed1b8ddbc16afab6bd4d76751.jpeg"
          alt="Retro American bald eagle with vintage wood-stock rifles over the American flag"
          className="w-full object-contain"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-ink" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink to-transparent" />
      </motion.div>

      <div className="relative z-20 mx-auto max-w-[1400px] px-5 md:px-10 min-h-screen flex flex-col justify-center pt-24">
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.6 }}
          data-testid="hero-name-banner"
          className="mb-5 inline-flex items-center gap-4 self-start flashy-banner border-[3px] border-gold px-7 md:px-10 py-4 md:py-6 shadow-[0_0_45px_rgba(224,30,43,0.6)] animate-glow-pulse"
        >
          <span className="neon-dot h-3 w-3 md:h-4 md:w-4 rounded-full bg-gold shadow-[0_0_14px_#ffd23f]" />
          <span className="font-display uppercase tracking-tight text-3xl sm:text-4xl md:text-6xl lg:text-7xl text-bone whitespace-nowrap leading-none">
            N.P. GunSmith &amp; Sales
          </span>
          <span className="neon-dot h-3 w-3 md:h-4 md:w-4 rounded-full bg-gold shadow-[0_0_14px_#ffd23f]" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          data-testid="hero-sot-badges"
          className="mb-7 flex flex-wrap items-center gap-3"
        >
          <span className="font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-gold border-2 border-gold bg-gold/10 px-4 py-2">
            ★ Class 3 · SOT Dealer
          </span>
          <span className="font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-bone border border-steel px-4 py-2">
            NFA Items · Suppressors · SBRs
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-brass" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-brass">
            Est. 1990 — {BUSINESS.city}
          </span>
        </motion.div>

        <motion.h1
          variants={lineWrap}
          initial="hidden"
          animate="show"
          className="font-display uppercase leading-[0.85] tracking-tighter text-[3.4rem] sm:text-7xl lg:text-8xl xl:text-9xl"
        >
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden py-1">
              <motion.span
                variants={lineChild}
                className={`block ${i === 1 ? "flashy-text animate-glow-pulse" : "text-bone"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 max-w-md font-body text-base md:text-lg text-bone/75 leading-relaxed"
        >
          Fort Mohave's trusted armory. Sales, service, and straight talk from
          folks who treat every customer like family.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-visit-btn"
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            className="font-mono text-xs uppercase tracking-[0.2em] bg-oxblood text-bone px-8 py-4 hover:bg-brass hover:text-ink transition-colors duration-200"
          >
            Visit The Shop
          </button>
          <button
            data-testid="hero-services-btn"
            onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
            className="font-mono text-xs uppercase tracking-[0.2em] border border-steel text-bone px-8 py-4 hover:border-brass hover:text-brass transition-colors duration-200"
          >
            Our Services
          </button>
          <div className="flex items-center gap-2 pl-2">
            <div className="flex text-brass">
              {[...Array(4)].map((_, i) => (
                <Star key={i} size={16} weight="fill" />
              ))}
              <Star size={16} weight="fill" className="opacity-40" />
            </div>
            <span className="font-mono text-xs text-bone/60">
              {BUSINESS.rating} · {BUSINESS.reviewCount} Yelp reviews
            </span>
          </div>
        </motion.div>
      </div>

      <motion.button
        style={{ opacity: fade }}
        onClick={scrollNext}
        data-testid="hero-scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-bone/50 hover:text-brass transition-colors"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={18} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
